import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { SessionProvider } from './contexts/session.context';
import { ThemeProvider } from './contexts/theme.context';
import { Home } from './pages/Home';
import { SessionUser, UserProvider } from './contexts/user.context';
import { useGetAuthenticatedUser } from './hooks/api/use-users.hook';
import { Login } from './pages/Login';
import { OntologyNodeDetails } from './pages/OntologyNodeDetails';
import { AppLayout } from './layouts/AppLayout';
import { ConfirmProvider } from './contexts/confirm.context';
import { Confirm } from './components/Confirm/Confirm';
import { OntologyLayout } from './layouts/OntologyLayout';
import { Users } from './pages/Users';
import { LoadingIndicator } from './components/LoadingIndicator';
import { AddEditUser } from './components/Users/AddEditUser';
import { Roles } from './pages/Roles';
import { AddEditRole } from './components/Roles/AddEditRole';
import { Domains } from './pages/Domains';
import { AddEditDomain } from './components/Domains/AddEditDomain';
import { Contracts } from './pages/Contracts';
import { CreateAutodeskTemplateFile } from './pages/CreateAutodeskTemplateFile';
import { AddEditContract } from './components/Contracts/AddEditContract';
import { AutodeskLayers } from './pages/AutodeskLayers';
import { Symbols } from './pages/Symbols';
import { FeatureTypes } from './pages/FeatureTypes';
import { AddEditFeatureType } from './components/FeatureTypes/AddEditFeatureType';
import { AddEditNode } from './components/Ontology/AddEditNode';
import { AddEditAttribute } from './components/Ontology/AddEditAttribute';
import { Aashtoware } from './pages/Aashtoware';
import { AddEditAashtoware } from './components/Aashtoware/AddEditAashtoware';
import { Settings } from './pages/Settings';
import { AddEditAutodeskLayer } from './components/AutodeskLayers/AddEditAutodeskLayer';
import { TrimbleLayers } from './pages/TrimbleLayers';
import { Blocks } from './pages/Blocks';
import { AddEditTrimbleLayer } from './components/TrimbleLayers/AddEditTrimbleLayer';
import { AddBlocks } from './components/Blocks/AddBlocks';
import { Sync } from './pages/Sync';
import { Templates } from './pages/Templates';
import { ExportSyncDraft } from './components/Sync/ExportSyncDraft';
import { ImportSymbols } from './components/Symbols/ImportSymbols';
import { ExportSymbols } from './components/Symbols/ExportSymbols';
import { EditBlock } from './components/Blocks/EditBlock';
import { RecoverPassword } from './components/Authentication/RecoverPassword';
import { ResetPassword } from './components/Authentication/ResetPassword';
import { ChangePassword } from './components/Authentication/ChangePassword';
import { Contact } from './components/Contact/Contact';
import { ExportArcgisSchema } from './components/ArcgisSchemas/ExportArcgisSchema';
import { AddEditSymbol } from './components/Symbols/AddEditSymbol';
import { PointStyles } from './pages/PointStyles';
import { AddEditPointStyle } from './components/PointStyles/AddEditPointStyle';
import { PointLabelStyles } from './pages/PointLabelStyles';
import { AddEditPointLabelStyle } from './components/PointLabelStyles/AddEditPointLabelStyle';
import { SettingConfigurations } from './pages/ArcgisCivil3dSettingConfigurations';
import { AddSettingConfiguration } from './components/ArcgisCivil3dSettings/Configurations/AddArcgisCivil3dSettingConfiguration';
import { EditSettingConfiguration } from './components/ArcgisCivil3dSettings/Configurations/EditArcgisCivil3dSettingConfiguration';
import { AddFeatureLineSetting } from './components/ArcgisCivil3dSettings/FeatureLines/AddArcgisCivil3dFeatureLineSetting';
import { AddCogoPointSetting } from './components/ArcgisCivil3dSettings/CogoPoints/AddArcgisCivil3dCogoPointSetting';
import { CogoPointPropertyMappings } from './components/ArcgisCivil3dSettings/PropertyMappings/CogoPointPropertyMappings';
import { FeatureLinePropertyMappings } from './components/ArcgisCivil3dSettings/PropertyMappings/FeatureLinePropertyMappings';
import { AddEditTemplate } from './components/Templates/AddEditTemplate';
import { EventLogs } from './pages/EventLogs';
import { EventLogDetails } from './components/EventLogs/EventLogDetails';
import { ManageTemplateFiles } from './components/Templates/ManageTemplateFiles';
import { CogoPointConfigurationGrid } from './components/ArcgisCivil3dSettings/CogoPoints/CogoPointConfigurationGrid';
import { FeatureLineConfigurationGrid } from './components/ArcgisCivil3dSettings/FeatureLines/FeatureLineConfigurationGrid';
import { AlignmentConfigurationGrid } from './components/ArcgisCivil3dSettings/Alignments/AlignmentConfigurationGrid';
import { AddAlignmentSetting } from './components/ArcgisCivil3dSettings/Alignments/AddArcgisCivil3dAlignmentSetting';
import { AlignmentPropertyMappings } from './components/ArcgisCivil3dSettings/PropertyMappings/AlignmentPropertyMappings';
import { ParcelConfigurationGrid } from './components/ArcgisCivil3dSettings/Parcels/ParcelConfigurationGrid';
import { AddParcelSetting } from './components/ArcgisCivil3dSettings/Parcels/AddArcgisCivil3dParcelSetting';
import { ParcelPropertyMappings } from './components/ArcgisCivil3dSettings/PropertyMappings/ParcelPropertyMappings';

export const App = () => {
  const { loading, user, error } = useGetAuthenticatedUser();

  if (loading) {
    return <LoadingIndicator />;
  }

  const authRoutes = [
    <Route key={'login'} path="*" element={<Login />}>
      <Route path="recover-password" element={<RecoverPassword />} />
      <Route path="password-reset" element={<ResetPassword />} />
    </Route>,
  ];

  const appRoutes = [
    <Route key={'home'} path="/" element={<AppLayout />}>
      <Route path="/" element={<OntologyLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/export-fxl" element={<ExportSyncDraft />} />
        <Route path="/export-arcgis-schema" element={<ExportArcgisSchema />} />
        <Route path="/ontology/:nodeType/:nodeId" element={<OntologyNodeDetails />}>
          <Route path="/ontology/:nodeType/:nodeId/add" element={<AddEditNode />} />
          <Route path="/ontology/:nodeType/:nodeId/edit" element={<AddEditNode />} />
          <Route path="/ontology/:nodeType/:nodeId/attributes/add" element={<AddEditAttribute />} />
          <Route path="/ontology/:nodeType/:nodeId/attributes/edit/:attributeId" element={<AddEditAttribute />} />
        </Route>
      </Route>
      ,
      <Route key={'event-logs'} path="/event-logs" element={<EventLogs />}>
        <Route path="/event-logs/:eventLogId" element={<EventLogDetails />} />
      </Route>
      ,
      <Route key={'users'} path="/users" element={<Users />}>
        <Route path="/users/add" element={<AddEditUser />} />
        <Route path="/users/edit/:userId" element={<AddEditUser />} />
      </Route>
      ,
      <Route key={'roles'} path="/roles" element={<Roles />}>
        <Route path="/roles/add" element={<AddEditRole />} />
        <Route path="/roles/edit/:roleId" element={<AddEditRole />} />
      </Route>
      ,
      <Route key={'domains'} path="/domains" element={<Domains />}>
        <Route path="/domains/add" element={<AddEditDomain />} />
        <Route path="/domains/edit/:domainId" element={<AddEditDomain />} />
      </Route>
      ,
      <Route key={'contracts'} path="/contracts" element={<Contracts />}>
        <Route path="/contracts/add" element={<AddEditContract />} />
        <Route path="/contracts/edit/:contractId" element={<AddEditContract />} />
      </Route>
      ,
      <Route key={'arcgis-civil3d-data-editor'} path="/arcgis-civil3d-data-editor">
        <Route path="" element={<SettingConfigurations />} />
        <Route path="add" element={<AddSettingConfiguration />} />
        <Route
          key={'arcgis-civil3d-data-editor-edit'}
          path="edit/:configurationSettingId"
          element={<EditSettingConfiguration />}
        >
          <Route key={'cogo-points'} path="cogo-points" element={<CogoPointConfigurationGrid />}>
            <Route key={'add'} path="add" element={<AddCogoPointSetting />} />
            <Route
              key={'property-mappings'}
              path=":cogoPointId/property-mappings"
              element={<CogoPointPropertyMappings />}
            />
          </Route>
          <Route key={'feature-lines'} path="feature-lines" element={<FeatureLineConfigurationGrid />}>
            <Route key={'add'} path="add" element={<AddFeatureLineSetting />} />
            <Route
              key={'property-mappings'}
              path=":featureLineId/property-mappings"
              element={<FeatureLinePropertyMappings />}
            />
          </Route>
          <Route key={'alignments'} path="alignments" element={<AlignmentConfigurationGrid />}>
            <Route key={'add'} path="add" element={<AddAlignmentSetting />} />
            <Route
              key={'property-mappings'}
              path=":alignmentId/property-mappings"
              element={<AlignmentPropertyMappings />}
            />
          </Route>
          <Route key={'parcels'} path="parcels" element={<ParcelConfigurationGrid />}>
            <Route key={'add'} path="add" element={<AddParcelSetting />} />
            <Route key={'property-mappings'} path=":parcelId/property-mappings" element={<ParcelPropertyMappings />} />
          </Route>
        </Route>
      </Route>
      ,
      <Route
        key={'create-civil-3d-template'}
        path="/create-civil-3d-template"
        element={<CreateAutodeskTemplateFile />}
      />
      ,
      <Route key={'trimble-layers'} path="/trimble-layers" element={<TrimbleLayers />}>
        <Route path="/trimble-layers/add" element={<AddEditTrimbleLayer />} />
        <Route path="/trimble-layers/edit/:trimbleLayerId" element={<AddEditTrimbleLayer />} />
      </Route>
      ,
      <Route key={'cad-blocks'} path="/cad-blocks" element={<Blocks />}>
        <Route path="/cad-blocks/add" element={<AddBlocks />} />
        <Route path="/cad-blocks/edit/:blockId" element={<EditBlock />} />
      </Route>
      ,
      <Route key={'cad-layers'} path="/cad-layers" element={<AutodeskLayers />}>
        <Route path="/cad-layers/add" element={<AddEditAutodeskLayer />} />
        <Route path="/cad-layers/edit/:autodeskLayerId" element={<AddEditAutodeskLayer />} />
      </Route>
      ,
      <Route key={'civil-3d-point-styles'} path="/civil-3d-point-styles" element={<PointStyles />}>
        <Route path="/civil-3d-point-styles/add" element={<AddEditPointStyle />} />
        <Route path="/civil-3d-point-styles/edit/:pointStyleId" element={<AddEditPointStyle />} />
      </Route>
      ,
      <Route key={'civil-3d-point-label-styles'} path="/civil-3d-point-label-styles" element={<PointLabelStyles />}>
        <Route path="/civil-3d-point-label-styles/add" element={<AddEditPointLabelStyle />} />
        <Route path="/civil-3d-point-label-styles/edit/:pointLabelStyleId" element={<AddEditPointLabelStyle />} />
      </Route>
      ,
      <Route key={'symbols'} path="/symbols" element={<Symbols />}>
        <Route path="import" element={<ImportSymbols />} />
        <Route path="export" element={<ExportSymbols />} />
      </Route>
      <Route key={'symbols-add'} path="/symbols/add" element={<AddEditSymbol />} />
      <Route key={'symbols-edit'} path="/symbols/edit/:symbolId" element={<AddEditSymbol />} />,
      <Route key={'aashtoware'} path="/aashtoware" element={<Aashtoware />}>
        <Route path="/aashtoware/add" element={<AddEditAashtoware />} />
        <Route path="/aashtoware/edit/:aashtowareId" element={<AddEditAashtoware />} />
      </Route>
      ,
      <Route key={'feature-types'} path="/feature-types" element={<FeatureTypes />}>
        <Route path="/feature-types/add" element={<AddEditFeatureType />} />
        <Route path="/feature-types/edit/:featureTypeId" element={<AddEditFeatureType />} />
      </Route>
      ,
      <Route key={'settings'} path="/settings" element={<Settings />} />
      <Route key={'sync'} path="/sync" element={<Sync />}>
        <Route path="/sync/export" element={<ExportSyncDraft />} />
      </Route>
      <Route key={'templates'} path="/templates" element={<Templates />}>
        <Route path="/templates/add" element={<AddEditTemplate />} />
        <Route path="/templates/edit/:templateId" element={<AddEditTemplate />} />
        <Route path="/templates/edit/:templateId/files" element={<ManageTemplateFiles />} />
      </Route>
    </Route>,
  ];

  return (
    <ThemeProvider>
      <HelmetProvider>
        <SessionProvider error={error?.response?.data?.message}>
          <UserProvider user={user ? new SessionUser(user) : undefined}>
            <ConfirmProvider>
              <Helmet defaultTitle="DPC" titleTemplate="%s | DPC" />
              <Confirm />
              <BrowserRouter>
                <Routes>{!user ? authRoutes : appRoutes}</Routes>
              </BrowserRouter>
            </ConfirmProvider>
          </UserProvider>
        </SessionProvider>
      </HelmetProvider>
    </ThemeProvider>
  );
};
